/**
 * @class VideoPlayer
 * @extends HTMLElement
 *
 * A custom web component for rendering video content using the Plyr library.
 * Supports HTML5 video as well as embedded YouTube and Vimeo sources.
 *
 * Plyr.js is used to provide a consistent, accessible player UI across all sources.
 */
export class VideoPlayer extends HTMLElement {
  #plyr = null;

  get host() {
    return this.dataset.host || 'html';
  }

  connectedCallback() {
    this.#initPlayer();
  }

  disconnectedCallback() {
    this.#destroyPlayer();
  }

  #initPlayer() {
    const targetElement = this.host === 'html'
      ? this.querySelector('video')
      : this.querySelector('[data-ref="plyrEmbed"]');

    if (!targetElement) return;

    this.#plyr = new Plyr(targetElement, this.#buildPlyrOptions(targetElement));
  }

  #destroyPlayer() {
    this.#plyr?.destroy();
    this.#plyr = null;
  }

  #buildPlyrOptions(targetElement) {
    const trackElement = targetElement.querySelector('track[kind="captions"]');
    const hasCaptions = trackElement !== null;

    const merchantConfig = this.#parseConfigAttribute();

    const baseControls = [
      'play-large',
      'play',
      'progress',
      'current-time',
      'mute',
      ...(hasCaptions ?
        ['captions', 'settings'] : []
      ),
      'fullscreen'
    ];

    const captionsConfig = hasCaptions
      ? {
        captions: {
          active: true,
          language: trackElement.srclang || 'auto',
          update: true
        }
      }
      : {};

    return {
      playsinline: true,
      hideControls: false,
      controls: baseControls,
      ...captionsConfig,
      ...merchantConfig
    };
  }

  /**
   * Optionally accept a full Plyr config object via `data-plyr-config='{ "title": "Example Title" }'`
   * for advanced overrides without changing the JS source.
   */
  #parseConfigAttribute() {
    const rawConfig = this.dataset.plyrConfig;

    if (!rawConfig) return {};

    try {
      return JSON.parse(rawConfig);
    } catch {
      return {};
    }
  }

  play() {
    this.#plyr?.play();
  }

  pause() {
    this.#plyr?.pause();
  }

  get paused() {
    return this.#plyr?.paused ?? true;
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer);
}
